import { Job, SupabaseJob } from '@applicator/shared'

import { findJobById, getFirstJob } from '../data'
import { mapSupabaseToJob } from '../helpers'

// Helper function to set selected job ID
export function setSelectedJobIdFromJob(
  job: Job | undefined,
  setSelectedJobId: React.Dispatch<React.SetStateAction<string | null>>
) {
  if (job?.id) {
    setSelectedJobId(job.id)
  }
}

// Helper function to set selected job from mapped jobs
export function setSelectedJobFromMapped(
  mappedJobs: Job[],
  id: string | undefined,
  setSelectedJobId: React.Dispatch<React.SetStateAction<string | null>>
) {
  if (id) {
    const foundJob = findJobById(mappedJobs, id)
    if (foundJob) {
      setSelectedJobIdFromJob(foundJob, setSelectedJobId)
    } else {
      setSelectedJobIdFromJob(getFirstJob(mappedJobs), setSelectedJobId)
    }
  } else {
    setSelectedJobIdFromJob(getFirstJob(mappedJobs), setSelectedJobId)
  }
}

// Helper function to setup loading timeout
export function setupLoadingTimeout(
  setError: React.Dispatch<React.SetStateAction<string | null>>,
  setLoading: React.Dispatch<React.SetStateAction<boolean>>
): NodeJS.Timeout {
  return setTimeout(() => {
    console.warn('[JobDetail] Loading timeout after 30 seconds')
    setError('Loading timeout. Please refresh the page.')
    setLoading(false)
  }, 30000)
}

// Helper function to process loaded jobs
export function processLoadedJobs(
  supabaseJobsData: SupabaseJob[],
  setJobs: React.Dispatch<React.SetStateAction<Job[]>>,
  setSupabaseJobs: React.Dispatch<React.SetStateAction<SupabaseJob[]>>,
  setAllJobsLoaded: React.Dispatch<React.SetStateAction<boolean>>,
  setSelectedJobFromMapped: (mappedJobs: Job[]) => void
) {
  const mappedJobs = supabaseJobsData.map((sbJob: SupabaseJob) => mapSupabaseToJob(sbJob))
  setJobs(mappedJobs)
  setSupabaseJobs(supabaseJobsData)
  setAllJobsLoaded(mappedJobs.length <= 20)
  setSelectedJobFromMapped(mappedJobs)
}

// Helper function to check if id changed and should trigger animation
export function shouldTriggerAnimation(currentId: string | undefined, prevId: string | undefined): boolean {
  return !!(currentId && currentId !== prevId && prevId !== undefined)
}

// Helper function to update supabaseJobs state
export function updateSupabaseJobsState(updatedJob: SupabaseJob, prev: SupabaseJob[]): SupabaseJob[] {
  const jobIndex = prev.findIndex(j => j.id === updatedJob.id)
  if (jobIndex >= 0) {
    // Job exists, update it
    const updated = [...prev]
    updated[jobIndex] = { ...updated[jobIndex], ...updatedJob }
    console.log('[JOB DETAIL] ✅ Updated job in state:', updated[jobIndex])
    return updated
  }
  // Job not in list, add it
  console.log('[JOB DETAIL] ➕ Adding new job to state:', updatedJob)
  return [...prev, updatedJob]
}

// Helper function to update jobs state for selected job
export function updateJobsStateForSelected(
  updatedJob: SupabaseJob,
  prev: Job[],
  supabaseJobs: SupabaseJob[]
): Job[] {
  const jobIndex = prev.findIndex(j => j.id === updatedJob.id)
  if (jobIndex >= 0) {
    const updated = [...prev]
    // Re-map the updated Supabase job to Job format
    const updatedSupabaseJob = supabaseJobs.find(j => j.id === updatedJob.id) || updatedJob
    const mappedJob = mapSupabaseToJob(updatedSupabaseJob)
    updated[jobIndex] = { ...updated[jobIndex], ...mappedJob }
    return updated
  }
  return prev
}
